import { UniqueWidgetRefreshContext } from 'App';
import EChartsMixed from 'components/Charts/EChartsMixed';
import MobileTabsHeader from 'components/TabsHeader/MobileTabsHeader';
import TabsHeader from 'components/TabsHeader/TabsHeader';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { usePerms } from 'contexts/PermsContext';
import { useAPIData } from 'hooks/useAPIData';
import useDataRefresh from 'hooks/useDataRefresh';
import Cookies from 'js-cookie';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  day7HourlyForecast,
  get15DayForecast,
  getDetailedForecastData,
} from 'services/weather/weather.service';
import * as chartUtils from 'utils/ChartUtils';
import { DateToString } from 'utils/stringUtils';
import { getWindArrow } from '../../utils/getWindArrowUtils.js';

const topTabs = [
  { option: '5-Day Detailed Forecast' },
  { option: '7-Day Hourly Forecast' },
  { option: '15-Day Forecast' },
  { option: 'Chance of Precipitation' },
  { option: 'Comfort Level' },
  { option: 'Dew Point' },
  { option: 'Humidity' },
  { option: 'Pressure' },
  { option: 'Precipitation' },
  { option: 'Temperature' },
  { option: 'Wind Speed' },
];

export default function Charts() {
  const [params] = useSearchParams();
  const [tabs, setTabs] = useState([0, 0]);
  const [detailedData, setDetailedData] = useState();
  const [day7Data, setDay7Data] = useState();
  const [day15Data, setDay15Data] = useState();
  const [precipData, setPrecipData] = useState();
  const [comfortLevelData, setComfortLevelData] = useState();
  const [dewPointData, setDewPointData] = useState();
  const [humidityData, setHumidityData] = useState();
  const [pressureData, setPressureData] = useState();
  const [precipitationData, setPrecipitationData] = useState();
  const [temperatureData, setTemperatureData] = useState();
  const [windSpeedChart, setWindSpeedChart] = useState();
  const FusionData = useAPIData();
  const navigate = useNavigate();
  const perms = usePerms();
  const dataRefresh = useDataRefresh();
  const uwf = useContext(UniqueWidgetRefreshContext);

  const ROUTE = 'Charts';
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;

  // Memoized tooltip callback to prevent ECharts re-render errors
  const windTooltipCallback = useMemo(
    () => ({
      label: (context) => {
        if (context.dataset.label?.includes('Wind Direction')) {
          return `${context.dataset.label}: ${context.raw?.displayValue || 'N/A'}`;
        }
        const value = context.parsed?.y;
        if (typeof value === 'number') {
          return `${context.dataset.label}: ${value.toFixed(2)}`;
        }
        return `${context.dataset.label}: ${value || 'N/A'}`;
      },
    }),
    [],
  );

  useEffect(() => {
    const initTab = [
      params.get('tab') ? parseInt(params.get('tab')) : 0,
      params.get('subtab') ? parseInt(params.get('subtab')) : 0,
    ];
    setTabs([...initTab]);
  }, []);

  useEffect(() => {
    (async () => {
      if (!(await perms.validatePermissions(['ChartsEnabled']))) {
        navigate('/404');
        return;
      }
    })();

    //get data
    FusionData.forceGet({ key: 'get15DayForecast', apiFunction: get15DayForecast }).then((res) => {
      const data = {
        precip: [],
        cloudCover: [],
        comfortLevel: [],
        dewPoint: [],
        humidity: [],
        pressure: [],
        precipitation: [],
        temperature: {
          //temperature is special because it needs 3 lines
          labels: [],
          datasets: [
            {
              label: 'High',
              data: [],
              borderColor: 'red',
            },
            {
              label: 'Mean',
              data: [],
              borderColor: 'yellow',
            },
            {
              label: 'Low',
              data: [],
              borderColor: 'blue',
            },
          ],
        },
        windSpeed: [],
      };

      //missing:
      //  cloud cover

      res.data.result.map((item) => {
        const dateStr = DateToString(new Date(item.iso8601));

        data.precip.push({
          label: `${dateStr.weekday_short}, ${dateStr.day_short} ${dateStr.month_short}`,
          dataPoint: item.precip_prob,
        });

        data.comfortLevel.push({
          label: `${dateStr.weekday_short}, ${dateStr.day_short} ${dateStr.month_short}`,
          dataPoint: item.comfort,
        });

        data.dewPoint.push({
          label: `${dateStr.weekday_short}, ${dateStr.day_short} ${dateStr.month_short}`,
          dataPoint: item.dew_point,
        });

        data.humidity.push({
          label: `${dateStr.weekday_short}, ${dateStr.day_short} ${dateStr.month_short}`,
          dataPoint: item.humidity,
        });

        data.pressure.push({
          label: `${dateStr.weekday_short}, ${dateStr.day_short} ${dateStr.month_short}`,
          dataPoint: item.baro_pressure,
        });

        data.precipitation.push({
          label: `${dateStr.weekday_short}, ${dateStr.day_short} ${dateStr.month_short}`,
          dataPoint: item.rainfall,
        });

        //temperature
        data.temperature.labels.push(
          `${dateStr.weekday_short}, ${dateStr.day_short} ${dateStr.month_short}`,
        );
        data.temperature.datasets[0].data.push(item.high_temp);
        data.temperature.datasets[1].data.push(
          (parseFloat(item.high_temp) + parseFloat(item.low_temp)) / 2,
        );
        data.temperature.datasets[2].data.push(item.low_temp);

        data.windSpeed.push({
          label: `${dateStr.weekday_short}, ${dateStr.day_short} ${dateStr.month_short}`,
          dataPoint: item.wind_speed,
          windDirection: item.wind_dir,
        });
      });

      setPrecipData({
        dataSets: {
          labels: data.precip.map((item) => {
            return item.label;
          }),
          datasets: [
            {
              type: 'bar',
              label: 'Chance of Precip.',
              data: data.precip.map((item) => {
                return item.dataPoint;
              }),
              backgroundColor: '#98ff18',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: true,
            text: 'Chance of Precip. (%)',
          },
        },
      });
      setComfortLevelData({
        dataSets: {
          labels: data.comfortLevel.map((item) => {
            return item.label;
          }),
          datasets: [
            {
              type: 'line',
              label: 'Comfort Level',
              data: data.comfortLevel.map((item) => {
                return item.dataPoint;
              }),
              borderColor: '#800080',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: true,
            text: 'Comfort Temperature (°F)',
          },
        },
      });
      setDewPointData({
        dataSets: {
          labels: data.dewPoint.map((item) => {
            return item.label;
          }),
          datasets: [
            {
              type: 'line',
              label: 'Dew Point',
              data: data.dewPoint.map((item) => {
                return item.dataPoint;
              }),
              borderColor: '#07fd08',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: true,
            text: 'Dew Point (°F)',
          },
        },
      });
      setHumidityData({
        dataSets: {
          labels: data.humidity.map((item) => {
            return item.label;
          }),
          datasets: [
            {
              type: 'bar',
              label: 'Humidity',
              data: data.humidity.map((item) => {
                return item.dataPoint;
              }),
              backgroundColor: '#07fd08',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: true,
            text: 'Relative Humidity (%)',
          },
        },
      });
      setPressureData({
        dataSets: {
          labels: data.pressure.map((item) => {
            return item.label;
          }),
          datasets: [
            {
              type: 'line',
              label: 'Pressure',
              data: data.pressure.map((item) => {
                return item.dataPoint;
              }),
              borderColor: '#000000',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: true,
            text: 'Pressure (")',
          },
        },
      });
      setPrecipitationData({
        dataSets: {
          labels: data.precipitation.map((item) => {
            return item.label;
          }),
          datasets: [
            {
              type: 'bar',
              label: 'Precipitation',
              data: data.precipitation.map((item) => {
                return item.dataPoint;
              }),
              backgroundColor: '#0304e9',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: true,
            text: 'Rainfall (in.)',
          },
        },
      });
      setTemperatureData({
        dataSets: {
          labels: data.temperature.labels.map((item) => {
            return item;
          }),
          datasets: [
            {
              type: 'line',
              label: 'High',
              data: data.temperature.datasets[0].data.map((item) => {
                return item;
              }),
              borderColor: 'red',
            },
            {
              type: 'line',
              label: 'Average',
              data: data.temperature.datasets[1].data.map((item) => {
                return item;
              }),
              borderColor: 'yellow',
            },
            {
              type: 'line',
              label: 'Low',
              data: data.temperature.datasets[2].data.map((item) => {
                return item;
              }),
              borderColor: 'blue',
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: true,
            text: 'Temperature (°F)',
          },
        },
      });

      const windSpeedPoints = data.windSpeed.map((item) => item.dataPoint);

      const windDirectionObjects = data.windSpeed.map((item) => {
        const arrow = getWindArrow(item.windDirection);
        return {
          x: undefined,
          y: item.dataPoint,
          windDirection: item.windDirection,
          displayValue: `${arrow} ${item.windDirection}°`,
        };
      });

      setWindSpeedChart({
        dataSets: {
          labels: data.windSpeed.map((item) => {
            return item.label;
          }),
          datasets: [
            {
              type: 'line',
              label: 'Wind Speed',
              borderColor: '#2129d6',
              data: windSpeedPoints,
              backgroundColor: '#2129d680',
              fill: true,
            },
            {
              type: 'line',
              label: 'Wind Direction',
              data: windDirectionObjects,
              borderColor: 'rgba(0,0,0,0)',
              showLine: false,
              showSymbol: false,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          title: {
            display: true,
            text: 'Wind Speed (MPH)',
          },
          plugins: {
            windDirectionIcon: {
              enabled: true,
              datasets: [1],
              iconStyle: { color: 'black' },
            },
            tooltip: {
              callbacks: windTooltipCallback,
            },
          },
        },
      });

      //chartjs object for mixed chart
      setDay15Data(chartUtils.Generate15DayChart(res.data.result));
    });

    // DETAILED FORECAST ------------------------------------------------------------------------------
    FusionData.forceGet({
      key: 'getDetailedForecastData',
      apiFunction: getDetailedForecastData,
    }).then((res) => {
      if (!res?.data?.result) return;

      res = res.data.result;

      //chartjs object for mixed chart
      setDetailedData(chartUtils.GenerateDetailedChart(res));
    });

    // 7 DAY ------------------------------------------------------------------------------
    FusionData.forceGet({ key: 'day7HourlyForecast', apiFunction: day7HourlyForecast })
      .then((res) => {
        res = res.data.result;

        setDay7Data(chartUtils.Generate7DayChart(res, {}, 4, 4));
      })
      .catch((e) => {});
  }, [dataRefresh.widgetRefresh, uwf[UNIQUEKEY]]);

  const renderTab = () => {
    switch (params.get('tab')?.toLowerCase()) {
      case '5-day detailed forecast':
        if (!detailedData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'bar'}
            dataSets={detailedData.dataSets}
            options={detailedData.options}
            plugins={detailedData.plugins}
          />
        );
      case '7-day hourly forecast':
        if (!day7Data) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'bar'}
            dataSets={day7Data.dataSets}
            options={day7Data.options}
            plugins={day7Data.plugins}
          />
        );
      case '15-day forecast':
        if (!day15Data) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'bar'}
            dataSets={day15Data.dataSets}
            options={day15Data.options}
            plugins={day15Data.plugins}
          />
        );
      case 'chance of precipitation':
        if (!precipData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed type={'bar'} dataSets={precipData.dataSets} options={precipData.options} />
        );
      case 'comfort level':
        if (!comfortLevelData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'line'}
            dataSets={comfortLevelData.dataSets}
            options={comfortLevelData.options}
          />
        );
      case 'dew point':
        if (!dewPointData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'line'}
            dataSets={dewPointData.dataSets}
            options={dewPointData.options}
          />
        );
      case 'humidity':
        if (!humidityData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'bar'}
            dataSets={humidityData.dataSets}
            options={humidityData.options}
          />
        );
      case 'pressure':
        if (!pressureData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'line'}
            dataSets={pressureData.dataSets}
            options={pressureData.options}
          />
        );
      case 'precipitation':
        if (!precipitationData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'bar'}
            dataSets={precipitationData.dataSets}
            options={precipitationData.options}
          />
        );
      case 'temperature':
        if (!temperatureData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'line'}
            dataSets={temperatureData.dataSets}
            options={temperatureData.options}
          />
        );
      case 'wind speed':
        if (!windSpeedChart) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'line'}
            dataSets={windSpeedChart.dataSets}
            options={windSpeedChart.options}
          />
        );
      default:
        if (!detailedData) return <WidgetLoadingAnimation />;
        return (
          <EChartsMixed
            type={'bar'}
            dataSets={detailedData.dataSets}
            options={detailedData.options}
            plugins={detailedData.plugins}
          />
        );
    }
  };

  return (
    <Container fluid className="d-flex flex-column h-100 py-3">
      {/* header */}
      <div className="d-none d-lg-block">
        <TabsHeader
          onTabChange={(newTabArr) => setTabs([...newTabArr])}
          currentTabs={tabs}
          options={topTabs}
          styles={[{ fontSize: 12 }]}
        />
      </div>
      <div className="d-block d-lg-none">
        <MobileTabsHeader options={topTabs} />
      </div>
      {/* content */}
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <WidgetTimeUpdate route={ROUTE} dataUpdateOnly addMargin />
        {renderTab()}
      </div>
    </Container>
  );
}
